/**
 * CEO Command Center - Knowledge Base Store
 * 
 * Store for managing organizational knowledge and documents.
 * Provides foundation for RAG (Retrieval Augmented Generation).
 * 
 * @module stores/knowledgeStore
 * @version 10.2.0
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

// ============================================
// TYPES
// ============================================

export interface KnowledgeItem {
  id: string;
  title: string;
  content: string;
  category: 'product' | 'sales' | 'finance' | 'hr' | 'operations' | 'strategy' | 'general';
  tags: string[];
  source?: string;
  sourceUrl?: string;
  createdAt: string;
  updatedAt: string;
  embedding?: number[]; // For future vector search
}

export interface KnowledgeCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  itemCount: number;
}

interface KnowledgeState {
  // Data
  items: KnowledgeItem[];
  
  // Actions
  addItem: (item: Omit<KnowledgeItem, 'id' | 'createdAt' | 'updatedAt'>) => KnowledgeItem;
  updateItem: (id: string, updates: Partial<KnowledgeItem>) => void;
  deleteItem: (id: string) => void;
  
  // Search
  search: (query: string, category?: string) => KnowledgeItem[];
  getByCategory: (category: string) => KnowledgeItem[];
  getByTags: (tags: string[]) => KnowledgeItem[];
  
  // Stats
  getCategoryStats: () => KnowledgeCategory[];
  
  // Bulk
  importItems: (items: Omit<KnowledgeItem, 'id' | 'createdAt' | 'updatedAt'>[]) => void;
  clearAll: () => void;
}

// ============================================
// UTILITIES
// ============================================

const generateId = () => `kb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const CATEGORIES: Record<string, { name: string; icon: string; description: string }> = {
  product: { name: 'Product', icon: '🔧', description: 'Product documentation and specs' },
  sales: { name: 'Sales', icon: '💼', description: 'Sales processes and materials' },
  finance: { name: 'Finance', icon: '💵', description: 'Financial policies and reports' },
  hr: { name: 'HR', icon: '👥', description: 'HR policies and procedures' },
  operations: { name: 'Operations', icon: '⚙️', description: 'Operational processes' },
  strategy: { name: 'Strategy', icon: '🎯', description: 'Strategic documents and plans' },
  general: { name: 'General', icon: '📄', description: 'General company knowledge' },
};

// Simple text search (can be replaced with vector search later)
function textSearch(items: KnowledgeItem[], query: string): KnowledgeItem[] {
  if (!Array.isArray(items)) return [];
  
  const queryLower = query.toLowerCase();
  const queryWords = queryLower.split(/\s+/).filter(w => w.length > 2);
  
  return items
    .map(item => {
      const titleLower = (item.title || '').toLowerCase();
      const contentLower = (item.content || '').toLowerCase();
      const tagsLower = Array.isArray(item.tags) ? item.tags.map(t => t.toLowerCase()).join(' ') : '';
      
      let score = 0;
      
      // Exact title match
      if (titleLower.includes(queryLower)) score += 10;
      
      // Word matches
      for (const word of queryWords) {
        if (titleLower.includes(word)) score += 5;
        if (contentLower.includes(word)) score += 2;
        if (tagsLower.includes(word)) score += 3;
      }
      
      return { item, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
}

// ============================================
// STORE
// ============================================

export const useKnowledgeStore = create<KnowledgeState>()(
  devtools(
    persist(
      (set, get) => ({
        items: [],
        
        // Add item
        addItem: (itemData) => {
          const item: KnowledgeItem = {
            id: generateId(),
            ...itemData,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          
          set(state => ({
            items: [...state.items, item],
          }));
          
          return item;
        },
        
        // Update item
        updateItem: (id, updates) => {
          set(state => ({
            items: state.items.map(item =>
              item.id === id
                ? { ...item, ...updates, updatedAt: new Date().toISOString() }
                : item
            ),
          }));
        },
        
        // Delete item
        deleteItem: (id) => {
          set(state => ({
            items: state.items.filter(item => item.id !== id),
          }));
        },
        
        // Search
        search: (query, category) => {
          let items = get().items || [];
          
          if (category) {
            items = items.filter(i => i.category === category);
          }
          
          return textSearch(items, query);
        },
        
        // Get by category
        getByCategory: (category) => {
          const items = get().items || [];
          return items.filter(i => i.category === category);
        },
        
        // Get by tags
        getByTags: (tags) => {
          const items = get().items || [];
          const tagsLower = tags.map(t => t.toLowerCase());
          return items.filter(item =>
            Array.isArray(item.tags) && item.tags.some(t => tagsLower.includes(t.toLowerCase()))
          );
        },
        
        // Get category stats
        getCategoryStats: () => {
          const items = Array.isArray(get().items) ? get().items : [];
          
          return Object.entries(CATEGORIES).map(([id, cat]) => ({
            id,
            name: cat.name,
            description: cat.description,
            icon: cat.icon,
            itemCount: items.filter(i => i && i.category === id).length,
          }));
        },
        
        // Import items
        importItems: (newItems) => {
          const items = newItems.map(itemData => ({
            id: generateId(),
            ...itemData,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }));
          
          set(state => ({
            items: [...state.items, ...items],
          }));
        },
        
        // Clear all
        clearAll: () => {
          set({ items: [] });
        },
      }),
      {
        name: 'sentiance_knowledge_base',
        version: 1,
        merge: (persistedState: any, currentState: any) => ({
          ...currentState,
          ...persistedState,
          // Ensure items is always an array
          items: Array.isArray(persistedState?.items) ? persistedState.items : [],
        }),
        onRehydrateStorage: () => (state, error) => {
          if (error) {
            console.error('Knowledge store rehydration error:', error);
            // Reset to empty on error
            if (state) {
              state.items = [];
            }
          }
        },
      }
    ),
    { name: 'ceo-knowledge-store' }
  )
);

// ============================================
// SELECTORS
// ============================================

export const useKnowledgeSearch = (query: string, category?: string) => {
  return useKnowledgeStore(state => {
    const items = Array.isArray(state.items) ? state.items : [];
    if (!query || query.length < 2) return items;
    return state.search(query, category) || [];
  });
};

export const useKnowledgeCategories = () => {
  return useKnowledgeStore(state => {
    try {
      if (typeof state.getCategoryStats !== 'function') {
        return [];
      }
      const stats = state.getCategoryStats();
      return Array.isArray(stats) ? stats : [];
    } catch (error) {
      console.error('Error getting category stats:', error);
      return [];
    }
  });
};
